import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProfileTab from './tabs/ProfileTab';
import TeamTab from './tabs/TeamTab';
import LLMKeysTab from './tabs/LLMKeysTab';
import BillingTab from './tabs/BillingTab';
import WebhooksTab from './tabs/WebhooksTab';
import AuditLogTab from './tabs/AuditLogTab';
import InstallPromptCard from './components/InstallPromptCard';
import useAppStore from '../../store';
import usePwaInstall from '../../hooks/usePwaInstall';

const TABS = [
  { id: 'profile', label: 'Profile', element: <ProfileTab /> },
  { id: 'team', label: 'Team', element: <TeamTab /> },
  { id: 'llm-keys', label: 'LLM keys', element: <LLMKeysTab /> },
  { id: 'billing', label: 'Billing', element: <BillingTab /> },
  { id: 'webhooks', label: 'Webhooks', element: <WebhooksTab /> },
  { id: 'audit-log', label: 'Audit log', element: <AuditLogTab /> }
];

/**
 * Settings screen (T10.1 + full-live-cutover §3 build). Tab selection lives
 * in the query string so every settings view is deep-linkable (refresh
 * restores state).
 *
 * §3 scope on this screen: (a) LLM Providers & Keys — LIVE over the L8
 * sealed store; (b) Business Profile — read-only posture (no profile write
 * API exists this batch; the tab says so); (c) Team — same posture; (d) PWA
 * install action — real beforeinstallprompt wiring; (e) Billing — honest
 * 501 with a graceful "not yet live" message. Bot-roster configuration is
 * deliberately NOT here (IA decision on record).
 */
function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const session = useAppStore((state) => state.session);
  const role = session?.role;
  const requested = searchParams.get('tab');
  const activeTab = TABS.some((tab) => tab.id === requested) ? requested : 'profile';
  const { canInstall, install } = usePwaInstall();

  return (
    <main className="page-content">
      <section className="page-heading">
        <div>
          <p className="eyebrow">CONTROL PLANE</p>
          <h1>Settings</h1>
          <span>
            Team, integrations, and governance for {role === 'agency-admin' ? 'your agency' : 'your account'}.
          </span>
        </div>
      </section>

      <InstallPromptCard canInstall={canInstall} onInstall={install} />

      <div className="settings-tabs" role="tablist" aria-label="Settings sections">
        {TABS.map((tab) => (
          <button
            aria-selected={activeTab === tab.id}
            className={activeTab === tab.id ? 'settings-tab-button active' : 'settings-tab-button'}
            key={tab.id}
            onClick={() => navigate(`/settings?tab=${tab.id}`, { replace: true })}
            role="tab"
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="settings-panel" role="tabpanel">
        {TABS.find((tab) => tab.id === activeTab)?.element}
      </div>
    </main>
  );
}

export default SettingsPage;
